import { useTranslation } from "react-i18next"
import VuiPageTitle from "../../../components/page-title"
import VuiAccessible from "../../../components/accessible"
import VuiButton from "../../../components/button"
import { PlusOutlined, SearchOutlined } from "@ant-design/icons"
import VuiModalConfirmation from "../../../components/modal-confirmation"
import { useCallback, useEffect, useState } from "react"
import { openNotification, overrideTableSortOrder } from "../../../utils/helpers"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { useFormShortLink } from "../hooks/use-form-short-link"
import useDebounce from "../../../hooks/use-debounce"
import { IPaginationParams } from "../../../utils/interfaces/pagination-params.interface"
import { DEFAULT_PAGE_SIZE } from "../../../constants"
import moment, { Moment } from "moment"
import { Col, Input, Row, Table } from "antd"
import { useGetShortLink } from "../hooks/use-get-short-link"
import VuiDateRangePicker from "../../../components/date-range-picker"
import { ColumnsType } from "antd/lib/table"
import { ShortLinkDataList } from "./interface"
import VuiTableActionButton from "../../../components/table-action-button"
import StatusTag from "../../../components/status-tag"

const qs = require("qs-lite");

function ShortLinkListModule() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    data,
    loadData,
    isOnFetchingData,
    totalData,
  } = useGetShortLink();

  const { onDelete, deleteLoading } = useFormShortLink();
  const [hasInitialize, setHasInitialize] = useState<boolean>(false);

  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 250);
  const [tableDeleteId, setTableDeleteId] = useState<number>(0);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tableQuery, setTableQuery] = useState<IPaginationParams>({
    page: Number(searchParams.get("page")) || 1,
    per_page: Number(searchParams.get("perPage")) || DEFAULT_PAGE_SIZE,
    sorted_by: searchParams.get("sortedBy"),
    order_by: searchParams.get("orderBy") as string,
  });
  const [selectedDate, setSelectedDate] = useState<[Moment, Moment]>([
    searchParams.get("start_date")
      ? moment(searchParams.get("start_date"), "YYYY-MM-DD")
      : moment().startOf("month"),
    searchParams.get("end_date")
      ? moment(searchParams.get("end_date"), "YYYY-MM-DD")
      : moment().endOf("month"),
  ]);

  const handleOnChangeDateRange = (dates: any) => {
    setSelectedDate(dates);
  };

  const handleChangeSearchParams = useCallback(async () => {
    const params = {} as IPaginationParams;

    Object.assign(params, {
      page: tableQuery.page,
    });

    Object.assign(params, {
      perPage: tableQuery.per_page,
    });

    if (debouncedSearch) {
      Object.assign(params, {
        search: debouncedSearch,
      });
    }

    if (tableQuery.order_by) {
      Object.assign(params, {
        orderBy: tableQuery.order_by,
      });
    }

    if (tableQuery.sorted_by) {
      Object.assign(params, {
        sortedBy: tableQuery.sorted_by,
      });
    }

    if (selectedDate.length > 0 && selectedDate[0] !== null) {
      Object.assign(params, {
        date_from: selectedDate[0].format("YYYY-MM-DD"),
        date_to: selectedDate[1].format("YYYY-MM-DD"),
      });
    }

    const queryParams = qs.stringify(params, { indices: false });

    if (queryParams) {
      setSearchParams(`?${queryParams}`);
    } else {
      navigate("/short-link");
    }

    const tableParams: IPaginationParams = {
      page: params.page,
      per_page: tableQuery.per_page,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
      ...(tableQuery.order_by
        ? { order_by: tableQuery.order_by }
        : { order_by: "created_at" }),
      ...(tableQuery.sorted_by
        ? { sorted_by: overrideTableSortOrder(tableQuery.sorted_by) }
        : { sorted_by: "DESC" }),
      ...(params.date_from ? { date_from: params.date_from } : {}),
      ...(params.date_to ? { date_to: params.date_to } : {}),
    };

    await loadData(tableParams);

    setHasInitialize(true);
  }, [
    tableQuery,
    debouncedSearch,
    selectedDate,
    loadData,
    navigate,
    setSearchParams,
  ]);

  const handleClickDelete = useCallback((id: number) => {
    setTableDeleteId(id)
    setShowDeleteModal(true)
  }, [])

  const columns: ColumnsType<ShortLinkDataList> = [
    {
      title: t("common.text.originalURL"),
      dataIndex: "original_url",
      key: "original_url",
    },
    {
      title: t("common.text.shortenedURL"),
      dataIndex: "shortened_url",
      key: "shortened_url",
    },
    {
      title: t("common.text.createdAt"),
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      defaultSortOrder:
        tableQuery.order_by === "created_at" && tableQuery.sorted_by,
      render: (date) => moment(date).format("DD MMM YYYY"),
    },
    {
      title: t("common.text.status"),
      dataIndex: "is_active",
      key: "is_active",
      sorter: true,
      defaultSortOrder:
        tableQuery.order_by === "is_active" && tableQuery.sorted_by,
      width: 150,
      render: (status) => <StatusTag active={status} />,
    },
    {
      title: t("common.text.action"),
      key: "id",
      className: "table-action",
      fixed: "right",
      width: 100,
      render: (text, record) => {
        return (
          <VuiTableActionButton
            detailPermissionName="short-link.show"
            deletePermissionName="short-link.destroy"
            detailLink={`/short-link/${record.id}`}
            onClickDelete={() => handleClickDelete(record.id)}
          />
        );
      },
    },
  ];

  const handleCloseModalDelete = useCallback(() => {
    setTableDeleteId(0)
    setShowDeleteModal(false)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    await onDelete(tableDeleteId).then(() => {
      handleCloseModalDelete()
      openNotification(
        "success",
        t("notification.success.deleteItem", {
          item: t("common.text.shortLink")
        })
      );

      handleChangeSearchParams();
    })
  }, [tableDeleteId, handleCloseModalDelete, t, handleChangeSearchParams])

  const handleTableChange = useCallback(
    (pagination: any, filters: any, sorter: any) => {
      const params: IPaginationParams = {
        page: pagination.current,
        per_page: pagination.pageSize,
      };

      if (sorter.order) {
        Object.assign(params, {
          sorted_by: sorter.order,
          order_by: sorter.field,
        });
      } else {
        Object.assign(params, {
          sorted_by: null,
          order_by: null,
        });
      }

      setTableQuery(params);
    },
    []
  );

  useEffect(() => {
    (async () => {
      await handleChangeSearchParams();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableQuery, selectedDate]);

  useEffect(() => {
    if (hasInitialize) {
      setTableQuery((prevState) => ({
        ...prevState,
        search: debouncedSearch,
        page: 1,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return (
    <>
      <VuiPageTitle title={t("pages.shortLink.list.title")}>
        <VuiAccessible access="short-link.store">
          <Link to="/short-link/add">
            <VuiButton
              title={t("common.button.addItem", {
                item: t("common.text.shortLink"),
              })}
              buttonProps={{
                icon: <PlusOutlined />,
                size: "middle",
              }}
            />
          </Link>
        </VuiAccessible>
      </VuiPageTitle>

      <VuiModalConfirmation
        show={showDeleteModal}
        isLoading={deleteLoading}
        onSubmit={handleConfirmDelete}
        onCancel={handleCloseModalDelete}
      />

      <Row gutter={[16, 16]} className="mb16">
        <Col className="gutter-row" xs={24} md={8}>
          <Input
            allowClear
            placeholder={t("common.form.search.placeholder")}
            prefix={<SearchOutlined />}
            value={search}
            size="middle"
            onChange={(value) => {
              setSearch(value.target.value);
            }}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} className="mb16">
        <Col className="gutter-row" xs={24} md={12}>
          <VuiDateRangePicker
            defaultValue={selectedDate}
            onChange={handleOnChangeDateRange}
          />
        </Col>
      </Row>

      <Table
        rowKey={"id"}
        columns={columns}
        dataSource={data}
        loading={isOnFetchingData}
        onChange={handleTableChange}
        pagination={{
          current: tableQuery.page,
          pageSize: tableQuery.per_page,
          total: totalData,
          showSizeChanger: true,
        }}
        size="small"
      />
    </>
  )
}

export default ShortLinkListModule